<template>
  <div class="wrapper" :class="{ 'nav-open': $sidebar.showSidebar }">
    <notifications></notifications>
    <sidebar-fixed-toggle-button />
    <side-bar :key="j"
      :background-color="sidebarBackground"
      :short-title="$t('sidebar.shortTitle')"
      :title="title"
      :logo="logo"
    >
      <!-- Brand row: the company and the active location in one switcher -->
      <template slot="brand">
        <group-switcher variant="sidebar" />
      </template>
      <template slot-scope="props" slot="links">
        <sidebar-item v-show="hasPermissions('dashboards_view') || hasPermissions('leaderboards_view')"
          :link="{
            name: $t('sidebar.dashboards'),
            icon: 'tim-icons icon-chart-pie-36',
            path: '/dashboard'
          }"
        >
          <sidebar-item v-show="hasPermissions('dashboards_view') || hasPermissions('leaderboards_view')"
            :link="{ name: $t('sidebar.gameDashboards'), path: '/dashboard/games' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('dashboards_view')"
                        :link="{ name: $t('sidebar.viewDashboards'), path: '/dashboards' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('reports_view')"
            :link="{ name: $t('sidebar.reportBuilder'), path: '/reports' }"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('schedules_view')"
                      :link="{
            name: $t('sidebar.schedules'),
            icon: 'tim-icons icon-molecule-40',
            path: '/bookings/schedules'
          }"
        >
        </sidebar-item>


        <sidebar-item v-show="hasPermissions('bookings_view') && featureEnabled(1)"
                      :link="{
            name: $t('sidebar.bookings'),
            icon: 'tim-icons icon-calendar-60',
            path: '/bookings'
          }"
        >
          <sidebar-item v-show="hasPermissions('bookings_view')"
            :link="{ name: $t('sidebar.upcoming-bookings'), path: '/bookings/list' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('bookings_view')"
                        :link="{ name: $t('sidebar.tickets'), path: '/bookings/tickets?count=10&page=1' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('bookings_view')"
                        :link="{ name: $t('sidebar.requests'), path: '/bookings/requests' }"
          ></sidebar-item>
          <!-- OTC-482 request forms: hidden until the feature ships; flip v-show back
               to hasPermissions('booking_site_update') to release it. -->
          <sidebar-item v-show="false"
                        :link="{ name: $t('sidebar.request-forms'), path: '/bookings/request-forms' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('booking_site_update')"
            :link="{ name: $t('sidebar.booking-settings'), path: '/bookings/settings/list'}"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('bookings_view') && !featureEnabled(1)"
                      :link="{
            name: $t('sidebar.bookings'),
            icon: 'tim-icons icon-calendar-60',
            path: '/bookings/settings/list'
          }"
        >
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('transactions_view') || hasPermissions('gift_cards_view')"
                      :link="{
            name: $t('sidebar.purchases'),
            icon: 'tim-icons icon-bag-16',
            path: '/bookings'
          }"
        >
          <sidebar-item v-show="hasPermissions('transactions_view')"
            :link="{ name: $t('sidebar.transactions'), path: '/purchases/transactions?count=10&page=1' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('transactions_view')"
                        :link="{ name: $t('sidebar.payments'), path: '/purchases/payments?count=10&page=1' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('gift_cards_view')"
            :link="{ name: $t('sidebar.gift_cards'), path: '/purchases/gift-cards?count=10&page=1&tab=0' }"
          ></sidebar-item>
          <!-- Passes hidden for now (2026-08-13) — restore by uncommenting.
          <sidebar-item is-beta v-show="hasPermissions('payments_configure') && passesBetaEnabled()"
                        :link="{ name: 'Passes', path: '/purchases/passes' }"
          ></sidebar-item>
          -->

          <sidebar-item v-show="hasPermissions('inventory_view')"
                        :link="{ name: $t('sidebar.inventory'), path: '/purchases/inventory?count=10&page=1' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('carts_view')"
                        :link="{ name: $t('sidebar.carts'), path: '/purchases/carts?count=10&page=1' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('promos_configure')"
                        :link="{ name: $t('sidebar.promos'), path: '/purchases/promos?count=10&page=1&archived=0' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('payments_configure')"
                        :link="{ name: $t('sidebar.payment-gateways'), path: '/purchases/settings?id=' + getPaymentSettingsId() }"
          ></sidebar-item>
          <sidebar-item is-beta v-show="hasPermissions('payment_pages_configure') && posBetaEnabled()"
                        :link="{ name: 'Point of sale', path: '/purchases/point-of-sale' }"
          ></sidebar-item>
        </sidebar-item>

        <!-- Limited beta. The entire marketing menu is company-gated: it shows
             only for companies in the affiliates (src/util/affiliatesBeta.js)
             or campaigns (src/util/campaignsBeta.js) allowlists. Children add
             their own gates. Affiliates permission gating (affiliates_view) is
             added at full launch once the permissions seed has run (phase 4.7). -->
        <sidebar-item is-beta v-show="affiliatesBetaEnabled() || isCampaignsBeta"
                      :link="{
            name: $t('sidebar.marketing'),
            icon: 'tim-icons icon-spaceship',
            path: '/marketing/affiliates'
          }"
        >
          <sidebar-item v-show="affiliatesBetaEnabled()"
            :link="{ name: $t('sidebar.affiliates'), path: '/marketing/affiliates' }"
          ></sidebar-item>
          <sidebar-item v-show="isCampaignsBeta && hasPermissions('custom_emails_campaigns_configure')"
            :link="{ name: $t('sidebar.campaigns'), path: '/marketing/campaigns'}"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('games_configure')
        || hasPermissions('game_history_view') || hasPermissions('hint_log_view') ||
        hasPermissions('game_tracker_submit') || hasPermissions('game_tracker_configure')"
          :link="{
            name: $t('sidebar.games'),
            icon: 'tim-icons icon-shape-star',
            path: '/games'
          }"
        >
          <sidebar-item v-show="hasPermissions('game_tracker_submit') && featureEnabled(2)"
            :link="{ name: $t('sidebar.gameTracker'), path: '/games/tracker/select' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('game_history_view') && featureEnabled(2)"
            :link="{ name: $t('sidebar.gameHistory'), path: '/games/history?count=10&page=1' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('game_tracker_configure') && featureEnabled(2)"
            :link="{ name: $t('sidebar.gameTrackerSettings'), path: '/games/tracker/settings?id=' + getGameTrackerSettingsId()}"
          ></sidebar-item>

          <sidebar-item v-if="hasPermissions('games_configure')"
            :link="{ name: $t('sidebar.gameSetup'), path: '/games/settings' }"
          ></sidebar-item>
          <sidebar-item is-legacy v-show="hasPermissions('hint_log_view') && featureEnabled(2)"
            :link="{ name: $t('sidebar.clues'), path: '/games/clues/select' }"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('checklists_view') && featureEnabled(3)"
          :link="{
            name: $t('sidebar.checklists'),
            icon: 'tim-icons icon-check-2',
            path: '/checklists'
          }"
        >
          <sidebar-item v-show="hasPermissions('checklists_view')"
            :link="{ name: $t('sidebar.activeChecklists'), path: '/checklists/new' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('checklists_view')"
            :link="{ name: $t('sidebar.checklistHistory'), path: '/checklists/history?count=10&page=1' }"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('project_view')
        || hasPermissions('time_log_create') || hasPermissions('time_log_view')"
                      :link="{
            name: $t('sidebar.projects'),
            icon: 'tim-icons icon-app',
            path: '/projects'

          }"
        >
          <sidebar-item v-show="hasPermissions('project_view')"
                        :link="{ name: $t('sidebar.projectList'), path: '/projects?count=10&page=1' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('time_log_create') || hasPermissions('time_log_view')"
            :link="{ name: $t('sidebar.timeTracker'), path: '/time-tracker/logs' }"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('checklists_view') && !featureEnabled(3)"
                      :link="{
            name: $t('sidebar.checklists'),
            icon: 'tim-icons icon-check-2',
            path: '/checklists/new'
          }"
        >
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('surveys_view')"
                      :link="{ name: $t('sidebar.surveys'), icon: 'tim-icons icon-pencil', path: '/surveys/view' }"
        >
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('photos_view') && featureEnabled(5)"
          :link="{
            name: $t('sidebar.photos'),
            icon: 'tim-icons icon-camera-18',
            path: '/photos'
          }"
        >
          <sidebar-item v-show="hasPermissions('photos_view')"
            :link="{ name: $t('sidebar.photos'), path: '/photos/new' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('photo_settings_update')"
            :link="{ name: $t('sidebar.photoSettings'), path: '/photos/settings?id='+ getPhotoSettingsId() }"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('photos_view') && !featureEnabled(5)"
                      :link="{
            name: $t('sidebar.photos'),
            icon: 'tim-icons icon-camera-18',
            path: '/photos'
          }"
        >
        </sidebar-item>

<!--        <sidebar-item v-show="hasPermissions('automated_emails_update')"-->
<!--                      :link="{ name: $t('sidebar.mail'), icon: 'tim-icons icon-email-85', path: '/mail' }"-->
<!--        >-->
<!--          <sidebar-item v-show="hasPermissions('automated_emails_update')"-->
<!--            :link="{ name: $t('sidebar.automatedEmails'), path: '/mail/automated/settings?id=' + getAutomatedEmailSettingsId()}"-->
<!--          ></sidebar-item>-->
<!--          <sidebar-item is-beta v-show="hasPermissions('custom_emails_templates_configure') || hasPermissions('custom_emails_automation_configure')"-->
<!--                        :link="{ name: $t('sidebar.customEmails'), path: '/mail/custom/landing'}"-->
<!--          ></sidebar-item>-->
<!--        </sidebar-item>-->

        <sidebar-item v-show="hasPermissions('automated_emails_update')"
                      :link="{ name: $t('sidebar.mail'), icon: 'tim-icons icon-email-85' }"
        >
          <sidebar-item v-show="hasPermissions('automated_emails_update')"
                        :link="{ name: $t('sidebar.automatedEmails'), path: '/mail/automated/settings?id=' + getAutomatedEmailSettingsId()}"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('custom_emails_templates_configure') || hasPermissions('custom_emails_automation_configure')"
                        :link="{ name: $t('sidebar.customEmails'), path: '/mail/custom/landing'}"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('automated_emails_update')"
                        :link="{ name: $t('sidebar.emailSettings'), path: '/mail/settings'}"
          ></sidebar-item>
          <sidebar-item is-beta v-show="isSmsBeta && hasPermissions('automated_emails_update')"
                        :link="{ name: $t('sidebar.smsSettings'), path: '/sms/settings'}"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('automated_emails_update')"
                        :link="{ name: $t('sidebar.messages'), path: '/mail/messages'}"
          ></sidebar-item>

        </sidebar-item>

        <sidebar-item v-show="hasPermissions('customer_view')"
                      :link="{ name: $t('sidebar.customers'), icon: 'tim-icons icon-user-run', path: '/customers?count=10&page=1' }"
        >
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('waivers_view') && featureEnabled(6)"
          :link="{ name: $t('sidebar.waivers'), icon: 'tim-icons icon-notes', path: '/waivers' }"
        >
          <sidebar-item v-show="hasPermissions('waivers_view')"
            :link="{ name: $t('sidebar.waiverHistory'), path: '/waivers/history?count=10&page=1' }"
          ></sidebar-item>
          <sidebar-item v-show="hasPermissions('waiver_settings_update')"
            :link="{ name: $t('sidebar.waiverSetup'), path: '/waivers/types' }"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item v-show="hasPermissions('waivers_view') && !featureEnabled(6)"
                      :link="{ name: $t('sidebar.waivers'), icon: 'tim-icons icon-notes', path: '/waivers/history?count=10&page=1' }"
        >
        </sidebar-item>

        <sidebar-item
          :link="{
            name: $t('sidebar.settings'),
            icon: 'tim-icons icon-settings-gear-63',
            path: '/settings'
          }"
        >
          <sidebar-item
            :link="{ name: $t('sidebar.myProfile'), path: '/settings/profile' }"
          ></sidebar-item>
          <sidebar-item
            :link="{ name: $t('sidebar.userManagement'), path: '/settings/user-management' }" v-show="hasPermissions('company_users_view')"
          ></sidebar-item>
          <sidebar-item
            :link="{ name: $t('sidebar.companySettings'), path: '/settings/company' }" v-show="hasPermissions('company_settings_configure')"
          ></sidebar-item>
          <sidebar-item
            :link="{ name: 'Custom fields and tags', path: '/settings/custom-fields' }" v-show="hasPermissions('custom_fields_configure')"
          ></sidebar-item>
          <sidebar-item
            :link="{ name: $t('sidebar.billing'), path: '/settings/billing' }" v-show="hasPermissions('billing_view')"
          ></sidebar-item>
          <sidebar-item
            :link="{ name: $t('sidebar.migration'), path: '/settings/migration' }" v-show="hasPermissions('migrations_view')"
          ></sidebar-item>
          <sidebar-item
            :link="{ name: $t('sidebar.logs'), path: '/settings/logs?count=10&page=1' }" v-show="hasPermissions('logs_view')"
          ></sidebar-item>
        </sidebar-item>

        <sidebar-item
          :link="{
            name: 'Platform resources',
            icon: 'tim-icons icon-bulb-63'
          }"
        >
          <sidebar-item
            :link="{ name: 'Support and roadmap', path: '/roadmap' }"
          ></sidebar-item>
          <sidebar-item
            :link="{ name: 'Setup wizard', path: '/wizard' }"
          ></sidebar-item>
        </sidebar-item>

      </template>
      <template slot="footer">
        <sidebar-user-menu />
      </template>
    </side-bar>
    <div id="mp" class="main-panel" :data="sidebarBackground">
      <!-- Phones and tablets: dims the page beside the open drawer; a tap closes it -->
      <div v-if="$sidebar.showSidebar" class="drawer-scrim d-xl-none" @click="toggleSidebar"></div>
      <dashboard-navbar></dashboard-navbar>
      <router-view name="header"></router-view>

      <div
        :class="{ content: !$route.meta.hideContent }"
        @click="toggleSidebar"
      >
        <zoom-center-transition :duration="200" mode="out-in">
          <!-- your content here -->
          <router-view></router-view>
        </zoom-center-transition>
      </div>
      <content-footer v-if="!$route.meta.hideFooter"></content-footer>
    </div>
    <onboarding-side-panel />

    <!-- Global "Send to POS payment in progress" tracker. Mounted at
         layout level so the drawer / minimized chip persists across
         routes — operator can navigate away while the customer taps,
         and the status follows them. -->
    <pos-payment-in-progress-drawer v-if="posBetaEnabled() && hasPermissions('payments_update')" />

    <!-- Global saved-views (saved filters) drawer. Mounted once at layout level so
         it can be opened from the navbar icon or any page's filter bar and persists
         across routes (like the POS drawer above). -->
    <saved-views-drawer />

    <!-- Location search palette (Ctrl K / Cmd K) and the full-page mask shown while switching -->
    <group-switcher variant="palette-host" />
    <loading-panel :loading="activeGroupState.switching" />

    <!-- Another tab switched location while this one had a drawer open or an
         append session running. The only way out is a reload. -->
    <base-alert-dialog
      :show="!!activeGroupState.externalChange"
      title="Location changed"
      variant="warning"
      confirm-text="Reload"
      :show-cancel="false"
      :dismissible="false"
      @confirm="reloadForGroupChange"
      @cancel="reloadForGroupChange"
    >
      This tab is showing {{ activeGroupState.externalChange && activeGroupState.externalChange.oldName }}. Your location changed to {{ activeGroupState.externalChange && activeGroupState.externalChange.newName }} in another tab. Reload to continue.
    </base-alert-dialog>

    <!-- Global Biku chat panel, opened from the navbar icon. Mounted once so the
         conversation and any in-flight reply survive route changes. -->
    <chat-assistant mode="global" />

    <!-- Trial ended modal -->
    <modal :show="showTrialEndedModal"
           :show-close="false"
           :closable="false"
           centered
           :class="{'modal-black': isDarkMode}"
           modal-classes="trial-ended-modal">
      <template slot="header">
        <h4 class="modal-title">
          <i class="fas fa-exclamation-triangle mr-2" style="color: #e14eca;"></i>
          Your trial has ended
        </h4>
      </template>
      <div>
        <p style="font-size: 15px;"><strong>Your trial with Off The Couch has expired.</strong></p>
        <p style="font-size: 15px;">To keep using the platform, please enter your billing information. If you need more time or have questions, contact
          <a href="mailto:nick@offthecouch.io">nick@offthecouch.io</a>.</p>
      </div>
      <template slot="footer">
        <div class="w-100 text-center pb-3">
          <base-button type="primary" @click="goToBilling">Go to billing</base-button>
        </div>
      </template>
    </modal>
  </div>
</template>
<script>
/* eslint-disable no-new */
import SidebarShare from './SidebarSharePlugin';
import DashboardNavbar from './DashboardNavbar.vue';
import ContentFooter from './ContentFooter.vue';
import DashboardContent from './Content.vue';
import SidebarFixedToggleButton from './SidebarFixedToggleButton.vue';
import { SlideYDownTransition, ZoomCenterTransition } from 'vue2-transitions';
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import _ from "lodash";
import OnboardingSidePanel from '@/pages/Wizard/OnboardingSidePanel';
import PosPaymentInProgressDrawer from '@/components/POS/PosPaymentInProgressDrawer';
import SavedViewsDrawer from '@/components/SavedViewsDrawer.vue';
import ChatAssistant from '@/pages/Wizard/ChatAssistant.vue';
import GroupSwitcher from '@/components/GroupSwitcher.vue';
import SidebarUserMenu from './SidebarUserMenu.vue';
import { state as activeGroupState, startCrossTabWatch } from '@/util/activeGroup';
import { isPosBetaCompany, canSendToPos } from '@/util/posBeta';
import { isAffiliatesBetaCompany } from '@/util/affiliatesBeta';
import { isPassesBetaCompany } from '@/util/passesBeta';
import { isCampaignsBetaCompany } from '@/util/campaignsBeta';
import { isSmsBetaCompany } from '@/util/smsBeta';
import posPaymentService from '@/services/posPayment';
import { getMe } from '@/services/groupData';

export default {
  components: {
    DashboardNavbar,
    ContentFooter,
    SidebarFixedToggleButton,
    DashboardContent,
    SlideYDownTransition,
    ZoomCenterTransition,
    SidebarShare,
    OnboardingSidePanel,
    PosPaymentInProgressDrawer,
    SavedViewsDrawer,
    ChatAssistant,
    GroupSwitcher,
    SidebarUserMenu
  },
  data() {
    return {
      sidebarBackground: 'brand', //brand (_sidebar-brand.scss), or vue|blue|orange|green|red|primary
      activeGroupState,
      title: '',
      logo: '',
      groupExists: false,
      j: 0,
      polling: null,
      features: [],
      showTrialEndedModal: false,
      trialExpired: false
    };
  },
  watch: {
    '$route'(to) {
      if (this.trialExpired) {
        this.showTrialEndedModal = to.path !== '/settings/billing'
      }
    }
  },
  computed: {
    isDarkMode() {
      return localStorage.getItem('mode') !== '0'
    },
    isCampaignsBeta() {
      return isCampaignsBetaCompany()
    },
    isSmsBeta() {
      return isSmsBetaCompany()
    }
  },
  methods: {
    reloadForGroupChange() {
      window.location.reload()
    },
    featureEnabled(feature_id){
      const matchFound = _.find(this.features, {id: feature_id})
      return matchFound
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    posBetaEnabled(){
      return isPosBetaCompany()
    },
    affiliatesBetaEnabled(){
      return isAffiliatesBetaCompany()
    },
    passesBetaEnabled(){
      return isPassesBetaCompany()
    },
    setTitle(){
      this.title = JSON.parse(localStorage.getItem('company')).name
      this.logo = JSON.parse(localStorage.getItem('company')).img

    },
    toggleSidebar() {
      if (this.$sidebar.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
    },
    isOTC(){
      let user = JSON.parse(localStorage.getItem('profile')).email
      if(user == 'jana.geddis+7@gmail.com' || user == 'nick@offthecouchgames.com' || user == 'ken@offthecouchgames.com'){
        return true
      }
      return false
    },
    getWaiverSettingsId(){
      if(JSON.parse(localStorage.getItem('group'))){
        return JSON.parse(localStorage.getItem('group')).waiver_configuration_id
      }
      return null
    },
    getCompanyGroupId(){
      if(JSON.parse(localStorage.getItem('group'))) {
        return JSON.parse(localStorage.getItem('group')).id
      }
      return null
    },
    getPaymentSettingsId(){
      if(JSON.parse(localStorage.getItem('group'))) {
        return JSON.parse(localStorage.getItem('group')).payment_settings_id
      }
      return null
    },
    getGameTrackerSettingsId(){
      if(JSON.parse(localStorage.getItem('group'))) {
        return JSON.parse(localStorage.getItem('group')).game_tracker_settings_id
      }
      return null
    },
    getPhotoSettingsId(){
      if(JSON.parse(localStorage.getItem('group'))) {
        return JSON.parse(localStorage.getItem('group')).photo_settings_id
      }

      return null
    },
    getBookingSiteSettingsId(){
      if(JSON.parse(localStorage.getItem('group'))) {
        return JSON.parse(localStorage.getItem('group')).booking_site_settings_id
      }
      return null
    },
    getAutomatedEmailSettingsId(){
      if(JSON.parse(localStorage.getItem('group'))) {
        return JSON.parse(localStorage.getItem('group')).automated_email_settings_id
      }
      return null
    },
    hasGroup(){
      if(localStorage.getItem('group')){
        this.groupExists = true
        clearInterval(this.polling);
        this.j++
      }
      this.groupExists  = false
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    goToBilling() {
      this.showTrialEndedModal = false
      this.$router.push('/settings/billing')
    },
    isOnBillingPage() {
      return this.$route.path === '/settings/billing'
    },
    async getProfile(){
      // Cached read shared with the navbar, so a hard load makes one users/me request
      getMe()
        .then(data => {
          this.features = data.features
          const trialEnd = data.company?.trial_end
          const hasBilling = data.has_billing

          if (trialEnd && new Date(trialEnd) > new Date()) {
            let trial = {
              trial_start: data.company.trial_start,
              trial_end: trialEnd
            }
            localStorage.setItem('trial', JSON.stringify(trial))
          } else {
            localStorage.removeItem('trial')
            if (hasBilling === false) {
              this.trialExpired = true
              if (!this.isOnBillingPage()) {
                this.showTrialEndedModal = true
              }
            }
          }

          localStorage.setItem('features', JSON.stringify(this.features))
          localStorage.setItem('permissions', JSON.stringify(data.permissions))
          localStorage.setItem('permissionLevels', JSON.stringify(data.permissionLevels || {}))
          this.checkCompanyInfoReminder()
        })
    },
    async checkCompanyInfoReminder(){
      if (sessionStorage.getItem('companyInfoReminderShown')) return
      if (this.showTrialEndedModal) return
      if (!this.hasPermissions('company_settings_configure')) return
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let url = API_LOCATION + 'companies/' + companyId
        let response = await axios.get(url, config)
        let company = response.data.company || {}
        const required = ['email', 'phone', 'address', 'city', 'zip']
        const incomplete = required.some(f => !company[f] || !String(company[f]).trim())
        if (!incomplete) return
        sessionStorage.setItem('companyInfoReminderShown', '1')
        swal({
          title: 'Complete your company info',
          text: 'Your company profile is missing contact details like email, phone, or address. This info is used across receipts, emails, and messaging setup.',
          type: 'info',
          showCancelButton: true,
          confirmButtonText: 'Go to settings',
          cancelButtonText: 'Later',
        }).then((result) => {
          if (result.value == true) {
            this.$router.push('/settings/company')
          }
        })
      } catch (err) {
        console.log('Company info reminder check failed', err)
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResizeSidebar);
    clearTimeout(this._resizeSidebarTimer);
  },
  mounted() {
    // Follow location switches made in other tabs (reload, or ask when this tab has unsaved work).
    startCrossTabWatch();
    // Folded or expanded sidebar, as last chosen on this device; re-applied when the window
    // crosses the 1200px drawer breakpoint
    this.$sidebar.restoreMinimize();
    this._onResizeSidebar = () => {
      clearTimeout(this._resizeSidebarTimer);
      this._resizeSidebarTimer = setTimeout(() => this.$sidebar.restoreMinimize(), 150);
    };
    window.addEventListener('resize', this._onResizeSidebar);
    this.getProfile()
    this.polling = setInterval(this.hasGroup, 100);
    this.setTitle();
    // Restore the POS in-progress drawer if the operator refreshed
    // mid-payment. Idempotent — safe to call on every mount. Gated to users
    // who can send POS orders so unauthorized users make no POS API calls.
    if (canSendToPos()) {
      posPaymentService.rehydrate();
    }
  }
};
</script>
<style lang="scss">
$scaleSize: 0.95;
@keyframes zoomIn95 {
  from {
    opacity: 0;
    transform: scale3d($scaleSize, $scaleSize, $scaleSize);
  }
  to {
    opacity: 1;
  }
}

.main-panel .zoomIn {
  animation-name: zoomIn95;
}

@keyframes zoomOut95 {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    transform: scale3d($scaleSize, $scaleSize, $scaleSize);
  }
}

.main-panel .zoomOut {
  animation-name: zoomOut95;
}

.trial-ended-modal {
  max-width: 600px;
}

.trial-ended-modal .modal-content {
  border-top: 3px solid #e14eca;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);
}

.trial-ended-modal .modal-header {
  border-bottom: 1.5px solid #e14eca;
  padding-bottom: 20px;
}

.modal.show:has(.trial-ended-modal) {
  background-color: rgba(0, 0, 0, 0.6);
}

/* total width */
body::-webkit-scrollbar {
  background-color: rgba(50, 50, 93, 0.24);
  width: 7px;
}

/* background of the scrollbar except button or resizer */
body::-webkit-scrollbar-track {
  background-color: rgba(50, 50, 93, 0.14);
}

/* scrollbar itself */
body::-webkit-scrollbar-thumb {
  background-color: rgba(218, 18, 18, 0.05);
  border-radius: 16px;
  border: 4px solid rgba(50, 50, 93, 0.79);
}

/* Sidebar: the dashboard theme paints a faded dot on the active parent menu in addition to the solid dot on the active child.
   That's redundant — the parent already shows it's active by being expanded with the chevron. Hide the parent dot so only the child has one. */
.sidebar .nav > li.active > a[data-toggle="collapse"]:before {
  display: none;
}
</style>
